import React, { useState, useEffect, Fragment, useCallback } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { setModal } from '../../redux/application-slice'
import {
  useGetRiskListSharesQuery,
  useShareRiskListTemplateMutation,
  useUnshareRiskListTemplateMutation,
} from '../../redux/api/project-risk-api-slice'
import {
  useGetOrganizationUsersQuery,
  useGetUserProfileQuery,
} from '../../redux/api-slice'
import { skipToken } from '@reduxjs/toolkit/query'
import { XMarkIcon } from '@heroicons/react/24/outline'
import posthog from 'posthog-js'

interface ShareRiskListModalProps {
  open: boolean
  riskTemplateId: number | string
  riskTemplateName: string
  canShare: boolean
}

export const ShareRiskListModal: React.FC<ShareRiskListModalProps> = ({
  open,
  riskTemplateId,
  riskTemplateName,
  canShare,
}) => {
  const dispatch = useDispatch()
  const [email, setEmail] = useState('')
  const [permission, setPermission] = useState('view')
  const [users, setUsers] = useState<
    Array<{ email: string; permission: 'view' | 'edit' }>
  >([])
  const [allSelected, setAllSelected] = useState(false)

  const {
    data: shareData,
    isLoading: isSharesLoading,
    isError: sharesError,
    refetch: refetchShares,
  } = useGetRiskListSharesQuery(open ? riskTemplateId : skipToken)

  const [shareRiskListTemplate, { isLoading }] =
    useShareRiskListTemplateMutation()
  const [unshareRiskListTemplate] = useUnshareRiskListTemplateMutation()

  const { data: userProfile } = useGetUserProfileQuery(undefined)
  const {
    data: organisationMembers,
    isLoading: isOrgMembersLoading,
    refetch: refetchOrgMembers,
  } = useGetOrganizationUsersQuery()

  // Refetch data when modal opens
  useEffect(() => {
    if (open) {
      refetchShares()
      refetchOrgMembers()
      setAllSelected(false)
    }
  }, [open, refetchShares, refetchOrgMembers])

  useEffect(() => {
    if (!open) return
    if (shareData) {
      const accepted = shareData.shared_users.map((u) => ({
        email: u.email,
        permission: u.permission,
      }))
      const pending = shareData.invited_users.map((email) => ({
        email,
        permission: 'view' as const,
      }))
      setUsers([...accepted, ...pending])
    }
  }, [open, shareData, sharesError])

  useEffect(() => {
    if (!canShare) {
      dispatch(setModal(null))
    }
  }, [canShare, dispatch])

  // Filter organization members
  const filteredOrgMembers = organisationMembers?.filter((member) => {
    // Filter out the current user
    if (member.email?.toLowerCase() === userProfile?.email?.toLowerCase()) {
      return false
    }

    // Filter out the creator
    if (
      shareData?.created_by &&
      member.email?.toLowerCase() === shareData.created_by.toLowerCase()
    ) {
      return false
    }

    // Filter out users who have pending invites
    if (
      shareData?.invited_users.some(
        (email) => email.toLowerCase() === member.email?.toLowerCase()
      )
    ) {
      return false
    }

    return true
  })

  const addInviteFromInput = useCallback(() => {
    const trimmed = email.trim().toLowerCase()
    if (!trimmed || !trimmed.includes('@')) {
      return
    }
    if (trimmed === userProfile?.email?.toLowerCase()) {
      return
    }
    if (users.find((u) => u.email.toLowerCase() === trimmed)) {
      return
    }
    setUsers((prev) => [
      ...prev,
      { email: trimmed, permission: permission as 'view' | 'edit' },
    ])
    setEmail('')
  }, [email, permission, users, userProfile])

  const toggleMemberSelection = useCallback(
    (memberEmail: string) => {
      // If the member is already in the users list, remove them
      // Otherwise, add them to the users list
      setUsers((prevUsers) => {
        const existingUserIndex = prevUsers.findIndex(
          (user) => user.email.toLowerCase() === memberEmail.toLowerCase()
        )

        if (existingUserIndex >= 0) {
          // Remove the user if they already exist
          return prevUsers.filter((_, index) => index !== existingUserIndex)
        } else {
          // Add the user with the current permission setting
          return [
            ...prevUsers,
            {
              email: memberEmail,
              permission: permission as 'view' | 'edit',
            },
          ]
        }
      })
    },
    [permission]
  )

  const handleRemoveInvite = useCallback(
    async (inviteEmail: string) => {
      try {
        const { unshared_users } = await unshareRiskListTemplate({
          id: riskTemplateId,
          payload: { users: [inviteEmail] },
        }).unwrap()
        if (unshared_users.includes(inviteEmail)) {
          setUsers((prev) =>
            prev.filter(
              (u) => u.email.toLowerCase() !== inviteEmail.toLowerCase()
            )
          )
        } else {
          setUsers((prev) =>
            prev.filter(
              (u) => u.email.toLowerCase() !== inviteEmail.toLowerCase()
            )
          )
        }
        // Refresh share data after unsharing
        refetchShares()
      } catch {
        toast.error('Error removing invite.')
      }
    },
    [riskTemplateId, unshareRiskListTemplate, refetchShares]
  )

  const handleShare = async () => {
    if (users.length === 0) {
      toast.error('Please add at least one email.')
      return
    }
    try {
      await shareRiskListTemplate({
        id: riskTemplateId,
        payload: { users },
      }).unwrap()

      // Force refetch data to update the cache
      await refetchShares()
      await refetchOrgMembers()

      toast.success('Shared successfully.')
      dispatch(setModal(null))
      posthog.capture('risk_list_shared', { templateId: riskTemplateId })
    } catch {
      toast.error('Failed to share.')
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      dispatch(setModal(null))
    }
  }

  // Check if a member is already in the users list
  const isMemberSelected = useCallback(
    (memberEmail: string): boolean => {
      return users.some(
        (user) => user.email.toLowerCase() === memberEmail.toLowerCase()
      )
    },
    [users]
  )

  // Toggle select all functionality
  const toggleSelectAll = useCallback(() => {
    if (!filteredOrgMembers) {
      return
    }

    setAllSelected((prev) => {
      const newAllSelected = !prev

      if (newAllSelected) {
        // Add all organization members to users
        setUsers((prevUsers) => {
          const currentEmails = prevUsers.map((u) => u.email.toLowerCase())
          const newUsers = [...prevUsers]

          filteredOrgMembers.forEach((member) => {
            if (
              member.email &&
              !currentEmails.includes(member.email.toLowerCase())
            ) {
              newUsers.push({
                email: member.email,
                permission: permission as 'view' | 'edit',
              })
            }
          })

          return newUsers
        })
      } else {
        // Remove all organization members from users
        setUsers((prevUsers) => {
          return prevUsers.filter((user) => {
            return !filteredOrgMembers.some(
              (member) =>
                member.email?.toLowerCase() === user.email.toLowerCase()
            )
          })
        })
      }

      return newAllSelected
    })
  }, [filteredOrgMembers, permission])

  // Check if all filterable org members are selected
  useEffect(() => {
    if (!filteredOrgMembers || filteredOrgMembers.length === 0) {
      setAllSelected(false)
      return
    }

    const allMembersSelected = filteredOrgMembers.every(
      (member) => member.email && isMemberSelected(member.email)
    )

    setAllSelected(allMembersSelected)
  }, [filteredOrgMembers, isMemberSelected, users])

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left shadow-xl transition-all">
                {/* X button in top right */}
                <button
                  type="button"
                  className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
                  onClick={handleClose}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>

                <Dialog.Title className="text-lg font-medium leading-6 text-gray-900">
                  Share Risk List Template
                </Dialog.Title>
                <div className="mt-4">
                  <p className="text-sm text-gray-500">
                    Invite users to share{' '}
                    <span className="font-semibold">{riskTemplateName}</span> by
                    entering their email or selecting users from your
                    organization.
                  </p>
                </div>

                {/* Manual email invite section */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="user@example.com"
                  />
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      onClick={addInviteFromInput}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Add Email
                    </button>
                  </div>
                </div>

                {/* Default Permission selector */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700">
                    Default Permission for New Invites
                  </label>
                  <select
                    value={permission}
                    onChange={(e) => setPermission(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="view">View</option>
                    <option value="edit">Edit</option>
                  </select>
                </div>

                {/* Organization Members List - with checkboxes */}
                <div className="mt-6">
                  <div className="mb-2 flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">
                      Select users from your organization
                    </label>
                    {filteredOrgMembers && filteredOrgMembers.length > 0 && (
                      <button
                        onClick={toggleSelectAll}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        {allSelected ? 'Deselect All' : 'Select All'}
                      </button>
                    )}
                  </div>

                  {isOrgMembersLoading || isSharesLoading ? (
                    <p className="mt-1 text-sm text-gray-500">
                      Loading members...
                    </p>
                  ) : filteredOrgMembers && filteredOrgMembers.length > 0 ? (
                    <ul className="mt-2 max-h-40 divide-y overflow-y-auto rounded-md border">
                      {filteredOrgMembers.map((member) => (
                        <li
                          key={member.id}
                          className="flex items-center px-3 py-2 hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            id={`member-${member.id}`}
                            checked={isMemberSelected(member.email as string)}
                            onChange={() =>
                              toggleMemberSelection(member.email as string)
                            }
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <label
                            htmlFor={`member-${member.id}`}
                            className="ml-3 block cursor-pointer text-sm font-medium text-gray-700"
                          >
                            {member.first_name} {member.last_name} (
                            {member.email})
                          </label>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-1 text-sm text-gray-500">
                      All organization members already have access or have been
                      invited.
                    </p>
                  )}
                </div>

                {/* Display invited users */}
                {users.length > 0 && (
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700">
                      People with Access
                    </label>
                    <ul className="mt-2 divide-y divide-gray-200 rounded-md border">
                      {users.map((invite, index) => {
                        // Find if they were already shared and what their original permission was
                        const sharedUser = shareData?.shared_users.find(
                          (u) =>
                            u.email.toLowerCase() === invite.email.toLowerCase()
                        )
                        const originalPermission = sharedUser?.permission

                        const isShared = Boolean(sharedUser)
                        const isPending = shareData?.invited_users.some(
                          (email) =>
                            email.toLowerCase() === invite.email.toLowerCase()
                        )
                        // New either if brand-new invite, or if we've changed an existing share’s permission
                        const isNew =
                          (!isShared && !isPending) ||
                          (isShared && invite.permission !== originalPermission)

                        return (
                          <li
                            key={invite.email}
                            className="flex items-center justify-between px-3 py-2"
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {invite.email}
                              </span>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500">
                                  Permission:
                                </span>
                                <select
                                  value={invite.permission}
                                  onChange={(e) =>
                                    setUsers((prev) =>
                                      prev.map((u, i) =>
                                        i === index
                                          ? {
                                              ...u,
                                              permission: e.target.value as
                                                | 'view'
                                                | 'edit',
                                            }
                                          : u
                                      )
                                    )
                                  }
                                  className="min-w-[100px] rounded-md border border-gray-300 bg-white px-3 py-1 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                  <option value="view">View</option>
                                  <option value="edit">Edit</option>
                                </select>

                                {/* only one badge at a time */}
                                {!isNew && isShared && (
                                  <span className="text-xs text-gray-400">
                                    • Active
                                  </span>
                                )}
                                {isPending && (
                                  <span className="text-xs text-gray-400">
                                    • Invited
                                  </span>
                                )}
                                {isNew && (
                                  <span className="text-xs font-semibold text-yellow-600">
                                    • New
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveInvite(invite.email)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}

                {/* Share button */}
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={handleShare}
                    disabled={isLoading || users.length === 0}
                    className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none"
                  >
                    {isLoading ? 'Sharing...' : 'Share'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
